/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { logger, redact } from "@/lib/utils/logger";
import type * as SentryTypes from "@sentry/nextjs";

// Cache resolved @sentry/nextjs module: `undefined` = not yet attempted, `null` = disabled or init failed
const SENTRY_MODULE_CACHE = Symbol.for("cc.sentry.moduleCache");
type GlobalWithSentryCache = typeof globalThis & {
  [SENTRY_MODULE_CACHE]?: typeof SentryTypes | null;
};

function getCachedSentryModule(): typeof SentryTypes | null | undefined {
  return (globalThis as GlobalWithSentryCache)[SENTRY_MODULE_CACHE];
}

function setCachedSentryModule(value: typeof SentryTypes | null): void {
  (globalThis as GlobalWithSentryCache)[SENTRY_MODULE_CACHE] = value;
}

async function ensureInit(): Promise<typeof SentryTypes | null> {
  const cached = getCachedSentryModule();
  if (cached !== undefined) {
    return cached;
  }

  const dsn = process.env["SENTRY_DSN"];
  // Avoid importing Sentry (and transitively OpenTelemetry) unless explicitly enabled or in production
  const enabled =
    process.env.NODE_ENV === "production" ||
    process.env["SENTRY_ENABLE"] === "true";
  if (!dsn || !enabled) {
    setCachedSentryModule(null);
    return null;
  }
  try {
    const Sentry = await import("@sentry/nextjs");
    // If already initialized elsewhere (e.g., sentry.*.config.ts), skip init
    try {
      // v10 API: Use getClient() instead of getCurrentHub().getClient()
      const existing = Sentry.getClient?.();
      if (existing) {
        setCachedSentryModule(Sentry);
        return Sentry;
      }
    } catch (error) {
      logger.warn("Failed to check existing Sentry client:", error);
    }
    // Initialize only once; basic setup
    Sentry.init({
      dsn,
      tracesSampleRate: Number(process.env["SENTRY_TRACES_SAMPLE_RATE"] ?? 0.1),
      environment: process.env.NODE_ENV || "development",
    });
    setCachedSentryModule(Sentry);
    return Sentry;
  } catch (error) {
    logger.error("Failed to initialize Sentry:", error);
    setCachedSentryModule(null);
    return null;
  }
}

/**
 * Report an exception to Sentry. The SDK treats exceptions as **error** severity by default;
 * callers do not need to pass a level. Context is passed through `redact()` before attachment.
 *
 * For **messages** (non-throwable signals), use {@link captureMessage} and pass an explicit
 * `level` when the event must page — see `docs/runbooks/sentry-alert-policy.md`.
 */
export async function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    const Sentry = await ensureInit();
    if (!Sentry) return;
    // v10 API: Use getCurrentScope().setContext() instead of setContext()
    if (
      context &&
      "getCurrentScope" in Sentry &&
      typeof Sentry.getCurrentScope === "function"
    ) {
      const scope = Sentry.getCurrentScope();
      if (
        scope &&
        "setContext" in scope &&
        typeof scope.setContext === "function"
      ) {
        scope.setContext("request", redact(context) as Record<string, unknown>);
      }
    }
    if (
      "captureException" in Sentry &&
      typeof Sentry.captureException === "function"
    ) {
      Sentry.captureException(error);
    }
  } catch (error) {
    logger.error("Failed to capture exception in Sentry:", error);
  }
}

/**
 * Wraps `Sentry.startSpan` when the SDK is available.
 *
 * **Alert policy:** On span setup failures, non-operation-like errors fall back to running
 * `fn` without a span (silent span drop). Operation-like errors (heuristic message match)
 * are rethrown. See `docs/runbooks/sentry-alert-policy.md` § Sentry infra boundaries — do not
 * assume every failure inside the callback becomes a Sentry issue.
 */
export async function startSpan<T>(
  name: string,
  fn: () => Promise<T>,
): Promise<T> {
  const Sentry = await ensureInit();
  if (!Sentry) {
    // Sentry not available - execute function without span
    return await fn();
  }

  if (!("startSpan" in Sentry) || typeof Sentry.startSpan !== "function") {
    // Sentry.startSpan not available - execute function without span
    return await fn();
  }

  // OPTIMIZATION: Distinguish between span creation errors and operation errors
  // If the operation inside the span fails, that's NOT a span creation error
  try {
    return await Sentry.startSpan({ name }, fn);
  } catch (error) {
    // Check if this is a span creation error or an operation error
    const errorMessage =
      error instanceof Error ? error.message || String(error) : String(error);

    // Operation errors (database queries, timeouts, etc.) should be re-thrown
    // They are NOT span creation failures
    const isOperationError =
      errorMessage.includes("Database") ||
      errorMessage.includes("query") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("connection") ||
      errorMessage.includes("failed") ||
      errorMessage.includes("error");

    if (isOperationError) {
      // This is an operation error, not a span creation error - re-throw it
      throw error;
    }

    // This might be a span creation error - log it and fallback to executing without span
    logger.error("Failed to start Sentry span:", {
      spanName: name,
      error: errorMessage,
      errorDetails:
        error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack?.split("\n").slice(0, 5).join("\n"),
            }
          : { raw: error },
    });

    // Fallback: execute function without span
    return await fn();
  }
}

export type SentrySeverity = "fatal" | "error" | "warning" | "info" | "debug";

/**
 * Send a structured **message** (not an exception) to Sentry.
 *
 * **Severity contract (CCP-636 / CCP-OBSERVABILITY-01):** Callers **MUST** pass `level` for any
 * event that should escalate above `info`. The default when `level` is omitted is the Sentry SDK
 * default for messages (`info`). Operational alarms (P0 billing drift, webhook failures, etc.)
 * that must notify on-call **must** use `"error"` or `"fatal"` explicitly — otherwise they will
 * not match paging alert rules. See `docs/runbooks/sentry-alert-policy.md` for the full
 * event-class → severity mapping and which Sentry alert rules consume each level.
 */
export type SentryBreadcrumbLevel =
  | "fatal"
  | "error"
  | "warning"
  | "info"
  | "debug";

/**
 * Add a Sentry breadcrumb when the SDK is available (same init gate as captureException).
 * Use for operational trail (e.g. slow LIST-SEARCH RPC); does not create an issue by itself.
 */
export async function addSentryBreadcrumb(input: {
  category: string;
  message: string;
  level?: SentryBreadcrumbLevel;
  data?: Record<string, unknown>;
}): Promise<void> {
  try {
    const Sentry = await ensureInit();
    if (!Sentry) return;
    if (!("addBreadcrumb" in Sentry) || typeof Sentry.addBreadcrumb !== "function") {
      return;
    }
    Sentry.addBreadcrumb({
      category: input.category,
      message: input.message,
      level: input.level ?? "info",
      data: input.data
        ? (redact(input.data) as Record<string, unknown>)
        : undefined,
    });
  } catch (error) {
    logger.error("Failed to add Sentry breadcrumb:", error);
  }
}

export async function captureMessage(
  message: string,
  context?: Record<string, unknown>,
  level?: SentrySeverity,
): Promise<void> {
  try {
    const Sentry = await ensureInit();
    if (!Sentry) return;
    // v10 API: Use getCurrentScope().setContext() instead of setContext()
    if (
      context &&
      "getCurrentScope" in Sentry &&
      typeof Sentry.getCurrentScope === "function"
    ) {
      const scope = Sentry.getCurrentScope();
      if (
        scope &&
        "setContext" in scope &&
        typeof scope.setContext === "function"
      ) {
        scope.setContext("context", redact(context) as Record<string, unknown>);
      }
    }
    if (
      "captureMessage" in Sentry &&
      typeof Sentry.captureMessage === "function"
    ) {
      if (level) {
        Sentry.captureMessage(message, level);
      } else {
        Sentry.captureMessage(message);
      }
    }
  } catch (error) {
    logger.error("Failed to capture message in Sentry:", error);
  }
}
